import React, { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getMemberPortalUrl } from "@/constants/links";

export default function MemberPortalSection() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOnHomePage = location.pathname === "/" || location.pathname === "";

  const handleContactClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (isOnHomePage) {
        const el = document.getElementById("contact");
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 56;
          window.scrollTo({ top, behavior: "smooth" });
        }
      } else {
        navigate("/#contact");
      }
    },
    [isOnHomePage, navigate],
  );

  return (
    <section id="member" className="w-full bg-[#f4f8fc] py-16 scroll-mt-14">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section label */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-0.5 bg-[#1a8fc1]"></div>
          <span className="text-[#1a8fc1] text-xs font-semibold tracking-widest uppercase">
            Member Access
          </span>
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Left column: heading + explanatory text + checklist */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-100">
                <i className="ri-shield-user-line text-lg text-[#0d1f4c]"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0d1f4c]">
                Member Portal
              </h2>
            </div>

            <div className="max-w-[560px] space-y-5">
              <p className="text-sm text-gray-500 leading-relaxed">
                Members can use the secure member portal to access available pension account information, including statements and other member resources.
              </p>

              <ul className="space-y-2.5">
                <li className="flex items-start gap-2.5 text-sm text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#1a8fc1] text-sm"></i>
                  </div>
                  <span>View available pension account information</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#1a8fc1] text-sm"></i>
                  </div>
                  <span>Access statements and member resources</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#1a8fc1] text-sm"></i>
                  </div>
                  <span>Use secure self-service access online</span>
                </li>
              </ul>


            </div>
          </div>

          {/* Right column: Portal Access card */}
          <div className="lg:w-[380px] flex-shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm border-t-[3px] border-t-[#1a8fc1] p-6">
              <h3 className="text-sm font-semibold text-[#0d1f4c] mb-4">
                Portal Access
              </h3>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2.5 text-xs text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-[#1a8fc1] text-xs"></i>
                  </div>
                  <span>Username: email address on file with MPF Canada</span>
                </li>
                <li className="flex items-start gap-2.5 text-xs text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-lock-password-line text-[#1a8fc1] text-xs"></i>
                  </div>
                  <span>Forgot password: use Forgot Password on the portal</span>
                </li>
                <li className="flex items-start gap-2.5 text-xs text-gray-500 leading-relaxed">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-question-line text-[#1a8fc1] text-xs"></i>
                  </div>
                  <span>First-time login? Need help? <a href="/#contact" className="text-[#1a8fc1] hover:underline transition-colors" onClick={handleContactClick}>Contact the Fund Office</a></span>
                </li>
              </ul>

              <a
                href={getMemberPortalUrl("en")}
                target="_blank"
                rel="nofollow"
                className="inline-flex items-center gap-2 w-full justify-center px-6 py-3 bg-[#1a8fc1] hover:bg-[#157ba6] text-white font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Access Web Account
                <i className="ri-arrow-right-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}